/**
 * Training pipeline run — Zod schemas & types
 *
 * A "run" represents a complete fine-tuning session (start → finish)
 * with hardware, config, dataset, and outcome metadata.
 */
import { z } from 'zod';
/** Hardware snapshot captured at run creation */
export const HardwareInfoSchema = z.object({
    machine: z.string(),
    cpu: z.string().optional(),
    gpu: z.object({
        model: z.string(),
        vram: z.string().optional(),
    }).optional(),
    ram: z.string().optional(),
    os: z.string().optional(),
});
/** Training configuration (LoRA-centric but extensible) */
export const TrainingConfigSchema = z.object({
    loraRank: z.number().optional(),
    loraAlpha: z.number().optional(),
    loraDropout: z.number().optional(),
    learningRate: z.number().optional(),
    batchSize: z.number().optional(),
    gradAccumSteps: z.number().optional(),
    warmupSteps: z.number().optional(),
    scheduler: z.string().optional(),
}).passthrough();
/** Dataset information */
export const DatasetInfoSchema = z.object({
    pairCount: z.number().optional(),
    description: z.string().optional(),
}).passthrough();
/** Outcome filled on completion */
export const RunOutcomeSchema = z.object({
    finalTrainLoss: z.string().optional(),
    finalValLoss: z.string().optional(),
    convergence: z.string().optional(),
    notes: z.string().optional(),
}).passthrough();
export const RunStatusSchema = z.enum(['running', 'completed', 'failed', 'stopped']);
/** Full training run entity (API response shape) */
export const TrainingRunSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable().optional(),
    status: RunStatusSchema,
    baseModel: z.string(),
    adapterType: z.string(),
    targetIterations: z.number().nullable().optional(),
    hardware: HardwareInfoSchema,
    trainingConfig: TrainingConfigSchema,
    dataset: DatasetInfoSchema.nullable().optional(),
    outcome: RunOutcomeSchema.nullable().optional(),
    startedAt: z.string(),
    completedAt: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Create request body */
export const CreateTrainingRunSchema = z.object({
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    baseModel: z.string().min(1),
    adapterType: z.string().default('lora'),
    targetIterations: z.number().int().positive().optional(),
    hardware: HardwareInfoSchema,
    trainingConfig: TrainingConfigSchema.default({}),
    dataset: DatasetInfoSchema.optional(),
    startedAt: z.string().datetime().optional(),
});
/** Patch request body */
export const UpdateTrainingRunSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().optional(),
    status: RunStatusSchema.optional(),
    targetIterations: z.number().int().positive().optional(),
    hardware: HardwareInfoSchema.optional(),
    trainingConfig: TrainingConfigSchema.optional(),
    outcome: RunOutcomeSchema.optional(),
    completedAt: z.string().datetime().optional(),
    dataset: DatasetInfoSchema.optional(),
}).refine(data => Object.keys(data).length > 0, {
    message: 'At least one field must be provided',
});
//# sourceMappingURL=training-run.js.map